import { useEffect, useState } from "react";
import { GeoJSON, useMap } from "react-leaflet";
import type { FeatureCollection, Geometry, GeoJsonProperties } from "geojson";

const WORLD_OUTLINE_URL = "https://cdn.jsdelivr.net/gh/martynafford/natural-earth-geojson@master/110m/physical/ne_110m_land.json";

interface OfflineBasemapProps {
  /**
   * Whether to show the offline basemap
   */
  show: boolean;
}

/**
 * OfflineBasemap - Simple world outline for offline use
 *
 * Loads a low-resolution world landmass outline from Natural Earth data.
 * This provides basic geographic context when tile servers are unavailable.
 * Renders in a custom pane below the overlay pane to stay behind quiz polygons.
 */
export function OfflineBasemap({ show }: OfflineBasemapProps) {
  const map = useMap();
  const [worldData, setWorldData] = useState<FeatureCollection<Geometry, GeoJsonProperties> | null>(null);
  const [error, setError] = useState(false);

  // Create a custom pane for the basemap at a lower z-index
  useEffect(() => {
    if (!map) return;

    // Create basemap pane if it doesn't exist
    if (!map.getPane('basemapPane')) {
      const pane = map.createPane('basemapPane');
      // Set z-index below overlayPane (default 400) but above tilePane (default 200)
      pane.style.zIndex = '250';
      console.log("[OfflineBasemap] Created basemapPane at z-index 250");
    }
  }, [map]);

  useEffect(() => {
    if (!show) return;

    // Try to load the world outline
    fetch(WORLD_OUTLINE_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setWorldData(data);
        console.log("[OfflineBasemap] World outline loaded");
      })
      .catch(err => {
        console.warn("[OfflineBasemap] Failed to load world outline:", err);
        setError(true);
      });
  }, [show]);

  if (!show || error) {
    return null;
  }

  if (!worldData) {
    return null;
  }

  return (
    <GeoJSON
      data={worldData}
      pane="basemapPane"
      style={{
        fillColor: "#f0f0f0",
        fillOpacity: 1,
        color: "#d0d0d0",
        weight: 0.5,
        opacity: 0.8,
      }}
      interactive={false}
    />
  );
}
